import lottieGetStartedCompletedCelebration from "@assets/lottie/get-started-completed-celebration.json";
import React from "react";
import { useI18n } from "@core/i18n/i18n";
import styled from "@emotion/styled";
import { DialogProps } from "primereact/dialog";
import { physioMeMobileBreakpoint } from "@ui/style";
import { BaseDialog } from "@ui/components/dialog/base-dialog";
import { CheckmarkIcon } from "@ui/components/common/checkmark-icon";
import { stack, row } from "@ui/style";
import { Button } from "@ui/components/buttons/button";
import { Player } from "@lottiefiles/react-lottie-player";
import { colors } from "@ui/style/colors";

export interface GetStartedCompletedDialogProps extends DialogProps {
	onClose: (closeModal: () => void) => void;
}

export const GetStartedCompletedDialog: React.FC<GetStartedCompletedDialogProps> = ({ onClose, ...otherProps }) => {
	const { format } = useI18n();

	return (
		<StyledDialog
			visible={true}
			dismissableMask={false}
			modal={false}
			position="bottom-right"
			blockScroll={false}
			showHeader={false}
			{...otherProps}
		>
			<AnimationContainer>
				<PlayerContainer>
					<StyledPlayer autoplay keepLastFrame src={lottieGetStartedCompletedCelebration} />
				</PlayerContainer>
			</AnimationContainer>
			<div className="p-size-l-bold">{format("getStarted.dialog.title")}</div>
			<div className="p-size-s">{format("getStarted.dialog.subTitle")}</div>
			<StepsContainer>
				<StyledStep step={format("getStarted.dialog.step.exercises")} />
				<StyledStep step={format("getStarted.dialog.step.programs")} />
				<StyledStep step={format("getStarted.dialog.step.patients")} />
			</StepsContainer>
			<StyledHr />
			<div className="p-size-s-bold">{format("getStarted.dialog.autoHideNotice")}</div>
			<ButtonContainer>
				<Button
					size="small"
					uiStyle="secondary_B"
					label={format("getStarted.dialog.closeButton")}
					onClick={() => onClose(otherProps.onHide)}
				/>
			</ButtonContainer>
		</StyledDialog>
	);
};

const Step: React.FC<{ step: string; className?: string }> = ({ step, className }) => (
	<div className={`p-size-s-bold ${className}`}>
		<CheckmarkIcon size="S" />
		{step}
	</div>
);

const StyledDialog = styled(BaseDialog)`
	position: fixed;
	margin-right: 56px;
	margin-bottom: 56px;
	width: 340px;

	.p-dialog-content {
		${stack(13, "flexstart", "stretch")};	
		padding: 0 24px 8px 24px;
		border-radius: 16px 16px 0 0;
	}

	@media ${physioMeMobileBreakpoint} {
		margin-right: 0;
		margin-bottom: 0;

	.p-dialog-content {
		border-radius: 0;
	}
`;

const AnimationContainer = styled.div`
	${row(undefined, "center")};
	position: relative;
	width: 100%;
	height: 135px;
	min-height: 135px;
`;

const PlayerContainer = styled.div`
	position: absolute;
	top: -45px;
`;

const StyledPlayer = styled(Player)`
	width: 100%;
	height: 254px;
`;

const StepsContainer = styled.div`
	${stack(13)};
	margin-left: 8px;
`;

const StyledStep = styled(Step)`
	${row("S", "flexstart", "center")};
`;

const StyledHr = styled.hr`
	color: ${colors.grey50};
	width: 55%;
`;

const ButtonContainer = styled.div`
	align-self: center;
`;
